import { ready } from '../../lib/lifecycle/ready';
import { getCurrentBreakpoint as getViewportBreakpoint, isMobileDevice } from '../../lib/utils/viewport';
import { debounce } from '../../lib/utils/debounce';
import {
  clampParallaxIntensity,
  parallaxBackgroundTranslateY,
  parallaxScrollProgress,
} from '../../lib/utils/parallaxBackgroundGeometry';

function getCurrentBreakpoint() {
  return getViewportBreakpoint(window.innerWidth);
}

function isEnabledForBreakpoint(container, breakpoint) {
  const breakpoints = container.dataset.fixedBgBreakpoints;
  if (!breakpoints) {
    return false;
  }

  try {
    const breakpointData = JSON.parse(breakpoints);
    return breakpointData[breakpoint] === true;
  } catch (e) {
    return false;
  }
}

function getBgElements(container) {
  return [
    container.querySelector('modulr-bg'),
    container.querySelector('video-bg'),
    container.querySelector('.modulr-background-gradient'),
  ].filter((el) => el !== null);
}

function clearBgParallaxStyles(bgElements) {
  bgElements.forEach((el) => {
    el.style.transform = '';
    el.style.height = '';
    el.style.top = '';
    el.style.willChange = '';
  });
}

function setupParallaxBgGeometry(container, bgElements) {
  const H = container.clientHeight;
  if (H <= 0) {
    return null;
  }

  const intensity = clampParallaxIntensity(container.dataset.fixedBgSpeed);
  const bgHeight = H * (1 + intensity);
  const topOffset = -(intensity * H) / 2;

  bgElements.forEach((el) => {
    el.style.height = `${bgHeight}px`;
    el.style.top = `${topOffset}px`;
    el.style.willChange = 'transform';
  });

  return { H, intensity, bgHeight };
}

function applyBgParallaxTranslate(container, cfg, bgElements) {
  const vh = window.innerHeight;
  const rect = container.getBoundingClientRect();
  const scrollY = window.pageYOffset;
  const progress = parallaxScrollProgress(rect, scrollY, vh);
  let translateY = parallaxBackgroundTranslateY(progress, cfg.intensity, cfg.H);

  const overshoot = Math.max(0, (cfg.bgHeight - cfg.H) / 2);
  if (overshoot > 0) {
    translateY = Math.max(-overshoot, Math.min(overshoot, translateY));
  }

  bgElements.forEach((el) => {
    el.style.transform = `translateY(${translateY}px)`;
  });
}

// Fixed mode: pin the 100vh background stack to the viewport (emulating
// background-attachment: fixed). The element sits at top:0 of the container, so
// translating it by -rect.top keeps it occupying the viewport while the
// container's overflow:hidden clips it to the section bounds.
function applyFixedBgTranslate(container, bgElements) {
  const rect = container.getBoundingClientRect();
  const translateY = -rect.top;
  bgElements.forEach((el) => {
    el.style.willChange = 'transform';
    el.style.transform = `translateY(${translateY}px)`;
  });
}

const reducedMotionMql = () => window.matchMedia('(prefers-reduced-motion: reduce)');

const fixedBgState = {
  containers: new Map(),
  active: new Set(),
  observer: null,
  ticking: false,
};

const parallaxState = {
  containers: new Map(),
  active: new Set(),
  observer: null,
  ticking: false,
};

function clearParallaxTransform(container) {
  const inner = container.querySelector('.parallax-inner');
  if (inner) {
    inner.style.transform = '';
  }
}

function parseParallaxOffset(raw, vh, vw) {
  if (!raw || raw === '0px') {
    return 0;
  }
  const m = String(raw).match(/^(-?[\d.]+)(px|vh|vw)$/);
  if (!m) {
    return 0;
  }
  const v = parseFloat(m[1]);
  if (m[2] === 'vh') {
    return (v / 100) * vh;
  }
  if (m[2] === 'vw') {
    return (v / 100) * vw;
  }
  return v;
}

function readParallaxConfig(container) {
  try {
    return {
      speed: JSON.parse(container.dataset.parallaxSpeed || '{}'),
      offset: JSON.parse(container.dataset.parallaxOffset || '{}'),
      breakpoints: JSON.parse(container.dataset.parallaxBreakpoints || '{}'),
    };
  } catch (e) {
    return { speed: {}, offset: {}, breakpoints: {} };
  }
}

function applyContainerVariationParallax() {
  if (reducedMotionMql().matches || !parallaxState.active.size) {
    return;
  }

  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const bp = getCurrentBreakpoint();

  parallaxState.active.forEach((container) => {
    const cfg = parallaxState.containers.get(container);
    const inner = container.querySelector('.parallax-inner');
    if (!cfg || !inner) {
      return;
    }
    if (cfg.breakpoints[bp] !== true) {
      inner.style.transform = '';
      return;
    }

    const rect = container.getBoundingClientRect();
    const scrollY = window.pageYOffset;
    const speed = typeof cfg.speed[bp] === 'number' ? cfg.speed[bp] : 0.5;
    const baseOffsetRaw = cfg.offset[bp] != null ? cfg.offset[bp] : '0px';
    const baseOffsetPx = parseParallaxOffset(baseOffsetRaw, vh, vw);

    const naturalBottom = rect.bottom + scrollY;
    const triggerScroll = Math.max(0, naturalBottom - vh);
    const maxDelta = rect.height + vh;
    const delta = Math.min(Math.max(0, scrollY - triggerScroll), maxDelta);
    const scrollOffset = delta * speed;

    inner.style.transform = `translateY(${scrollOffset + baseOffsetPx}px)`;
  });
}

function applyFixedBackgroundParallax() {
  if (reducedMotionMql().matches || !fixedBgState.active.size) {
    return;
  }

  const bp = getCurrentBreakpoint();

  fixedBgState.active.forEach((container) => {
    const entry = fixedBgState.containers.get(container);
    if (!entry) {
      return;
    }
    if (!isEnabledForBreakpoint(container, bp)) {
      clearBgParallaxStyles(entry.bgElements);
      return;
    }

    if (entry.mode === 'fixed') {
      applyFixedBgTranslate(container, entry.bgElements);
      return;
    }

    if (!entry.geometry) {
      entry.geometry = setupParallaxBgGeometry(container, entry.bgElements);
    }
    if (!entry.geometry) {
      return;
    }

    applyBgParallaxTranslate(container, entry.geometry, entry.bgElements);
  });
}

function destroyFixedBackgrounds() {
  fixedBgState.containers.forEach((entry, container) => {
    clearBgParallaxStyles(entry.bgElements);
  });
  fixedBgState.containers.clear();
  fixedBgState.active.clear();
  if (fixedBgState.observer) {
    fixedBgState.observer.disconnect();
    fixedBgState.observer = null;
  }
}

function initFixedBackgrounds() {
  destroyFixedBackgrounds();

  const containers = document.querySelectorAll('[data-has-fixed-bg="true"]');
  if (!containers.length) {
    return;
  }

  if (isMobileDevice()) {
    let hasMobileEnabled = false;
    containers.forEach((container) => {
      if (isEnabledForBreakpoint(container, '576')) {
        hasMobileEnabled = true;
      }
    });
    if (!hasMobileEnabled) {
      return;
    }
  }

  fixedBgState.observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          fixedBgState.active.add(e.target);
        } else {
          fixedBgState.active.delete(e.target);
          const entry = fixedBgState.containers.get(e.target);
          if (entry) {
            clearBgParallaxStyles(entry.bgElements);
            entry.geometry = null;
          }
        }
      });
      if (fixedBgState.active.size) {
        requestAnimationFrame(applyFixedBackgroundParallax);
      }
    },
    { rootMargin: '50% 0px 50% 0px' }
  );

  containers.forEach((container) => {
    const bgElements = getBgElements(container);
    if (bgElements.length === 0) {
      return;
    }

    const mode = container.dataset.fixedBgMode === 'parallax' ? 'parallax' : 'fixed';
    const entry = { mode, bgElements, geometry: null };
    fixedBgState.containers.set(container, entry);
    fixedBgState.observer.observe(container);

    const bp = getCurrentBreakpoint();
    if (mode === 'parallax' && isEnabledForBreakpoint(container, bp)) {
      entry.geometry = setupParallaxBgGeometry(container, bgElements);
    }
  });

  requestAnimationFrame(applyFixedBackgroundParallax);
}

function initParallaxContainers() {
  if (parallaxState.observer) {
    parallaxState.observer.disconnect();
    parallaxState.observer = null;
  }
  parallaxState.containers.forEach((_, c) => clearParallaxTransform(c));
  parallaxState.containers.clear();
  parallaxState.active.clear();

  const containers = document.querySelectorAll('[data-container-parallax="true"]');
  if (!containers.length) {
    return;
  }

  if (isMobileDevice()) {
    let hasMobileEnabled = false;
    containers.forEach((container) => {
      try {
        const bps = JSON.parse(container.dataset.parallaxBreakpoints || '{}');
        if (bps['576'] === true) {
          hasMobileEnabled = true;
        }
      } catch (e) {}
    });
    if (!hasMobileEnabled) {
      containers.forEach(clearParallaxTransform);
      return;
    }
  }

  parallaxState.observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          parallaxState.active.add(e.target);
        } else {
          parallaxState.active.delete(e.target);
          clearParallaxTransform(e.target);
        }
      });
      if (parallaxState.active.size) {
        requestAnimationFrame(applyContainerVariationParallax);
      }
    },
    { rootMargin: '50% 0px 50% 0px' }
  );

  containers.forEach((c) => {
    parallaxState.containers.set(c, readParallaxConfig(c));
    parallaxState.observer.observe(c);
  });

  requestAnimationFrame(applyContainerVariationParallax);
}

function initContainerView() {
  initFixedBackgrounds();
  initParallaxContainers();

  const onResize = debounce(() => {
    destroyFixedBackgrounds();
    initFixedBackgrounds();
    initParallaxContainers();
  }, 200);
  window.addEventListener('resize', onResize);

  let scrollTicking = false;
  const onScroll = () => {
    if (scrollTicking) {
      return;
    }
    scrollTicking = true;
    requestAnimationFrame(() => {
      applyFixedBackgroundParallax();
      applyContainerVariationParallax();
      scrollTicking = false;
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });

  const motionMql = reducedMotionMql();
  const onMotionPref = () => {
    if (motionMql.matches) {
      fixedBgState.containers.forEach((entry) => clearBgParallaxStyles(entry.bgElements));
      parallaxState.containers.forEach((_, c) => clearParallaxTransform(c));
    } else {
      applyFixedBackgroundParallax();
      applyContainerVariationParallax();
    }
  };
  motionMql.addEventListener('change', onMotionPref);

  return function destroy() {
    window.removeEventListener('resize', onResize);
    window.removeEventListener('scroll', onScroll);
    motionMql.removeEventListener('change', onMotionPref);
    if (parallaxState.observer) {
      parallaxState.observer.disconnect();
      parallaxState.observer = null;
    }
    parallaxState.containers.forEach((_, c) => clearParallaxTransform(c));
    parallaxState.containers.clear();
    parallaxState.active.clear();
    destroyFixedBackgrounds();
  };
}

ready(initContainerView, { scope: 'modulr-container' });
